import {
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { HighlightOff, SettingsBackupRestore } from '@material-ui/icons';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { Button } from '@components/Button';
import { Pagination } from '@material-ui/lab';
import { ContainerButtonsTop, DivSearch } from './styles';
import { NavLink } from '@components/NavLink';
import { UploadCSV } from '@components/UploadCSV';
import { maskCNPJ, maskCPF, verifyNumberOrString } from '../../utils/masks';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { situ } from '@data/colab';

interface IColaborador {
  cnh: string;
  cnpj: string;
  cod_ghe: string;
  cpf_colaborador: string;
  email: string;
  telefone: string;
  data_admissao: string;
  data_nascimento: string;
  funcao: string;
  genero: string;
  ghe: string;
  horario_trabalho: string;
  id_colaboradores: number;
  local_obra: string;
  local_trabalho: string;
  matricula_empresa: string;
  matricula_esocial: string;
  nis: string;
  nome_completo: string;
  razao_social: string;
  rg: string;
  setor: string;
  situacao: string;
  turno: string;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

const PER_PAGE = 30;

export const Coloboradores = () => {
  const [data, setData] = useState<IColaborador[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [situacao, setSituacao] = useState('');
  const [dialogDelete, setDialogDelete] = useState(false);
  const [edit, setEdit] = useState<IColaborador | null>(null);
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('success');
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Função para carregar dados
  const fetchData = async (
    page: number,
    search: string = '',
    situacao: string = ''
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: PER_PAGE,
        ...(search && { search }),
        ...(situacao && { situacao })
      };

      const response = await api.get(`ravim/colaboradores`, { params });

      if (response.data) {
        setData(response.data.results || response.data);
        setTotalPages(response.data.total_pages || 1);
        setTotalRecords(
          response.data.total_records || response.data.length || 0
        );
      }
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
      setSnack(true);
      setMessage('Erro ao carregar dados. Tente novamente.');
      setSeverity('error');
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Carrega dados iniciais
  useEffect(() => {
    fetchData(currentPage, searchValue, situacao);
  }, [currentPage, situacao]);

  // Cleanup do timeout
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Função para fechar o dialog
  const closeDialog = () => {
    setDialogDelete(false);
    setEdit(null);
  };

  // Função para abrir o dialog de exclusão
  const deleteColaborador = (colaborador: IColaborador) => {
    setEdit(colaborador);
    setDialogDelete(true);
  };

  // Manipuladores de eventos
  const handlePageChange = (_: unknown, newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  
  // Verifica se o valor parece ser um CPF (contém apenas números ou está formatado como CPF)
  const isPotentialCPF = /^[\d.-]*$/.test(value);
  
  let formattedValue = value;
  
  // Aplica máscara apenas se for um CPF
  if (isPotentialCPF) {
    const numericValue = value.replace(/\D/g, '');
    formattedValue = maskCPF(numericValue);
  }

  setSearchValue(formattedValue);
  setCurrentPage(1);

  // Debounce para evitar múltiplas requisições
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  // Só pesquisa se tiver pelo menos 3 caracteres ou se o campo foi limpo
  if (formattedValue.length >= 3 || formattedValue.length === 0) {
    setSearchTimeout(
      setTimeout(() => {
        fetchData(1, formattedValue, situacao);
      }, 500)
    );
  }
};

  const handleSituacaoChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setSituacao(e.target.value as string);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSituacao('');
    setCurrentPage(1);
    fetchData(1, '', '');
  };

  // Função para confirmar exclusão
  const confirmDelete = async () => {
    if (!edit) return;

    try {
      setLoading(true);
      const res = await api.delete(`ravim/exclu-colaboradores`, {
        data: { id_colaboradores: edit.id_colaboradores }
      });

      if (res.status === 200) {
        setSnack(true);
        setMessage(res.data.message || 'Colaborador excluído com sucesso');
        setSeverity('success');
        closeDialog();
        fetchData(currentPage, searchValue, situacao);
      }
    } catch (err: any) {
      setSnack(true);
      setMessage(err.response?.data?.message || 'Erro ao excluir colaborador');
      setSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSnackClose = () => {
    setSnack(false);
  };

  return (
    <Container maxWidth="xl" style={{ padding: '24px 0' }}>
      {/* Cabeçalho e controles */}
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            label="Pesquisar por Nome ou CPF"
            type="text"
            variant="outlined"
            value={searchValue}
            onChange={handleSearchChange}
            inputProps={{
              maxLength: verifyNumberOrString(searchValue) ? 14 : 100
            }}
            fullWidth
          />
          <FormControl variant="outlined" style={{ minWidth: 120 }}>
            <InputLabel>Situação</InputLabel>
            <Select
              label="Situação"
              value={situacao}
              onChange={handleSituacaoChange}
            >
              <MenuItem value="">Todos</MenuItem>
              {situ.map((item) => (
                <MenuItem key={item.id} value={item.value}>
                  {item.situacao}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(searchValue || situacao) && (
            <Button
              variantstyle="SECUNDARY"
              icon={<X fontSize="medium" />}
              onClick={handleClearSearch}
              style={{ marginLeft: '8px' }}
              title="Limpar filtros"
            />
          )}
        </DivSearch>
        <div style={{ display: 'flex', gap: 10 }}>
          <UploadCSV />
          <NavLink to="/cadastro-colaborador">
            <Button title="Cadastro Colaborador" />
          </NavLink>
        </div>
      </ContainerButtonsTop>

      {/* Título e contador */}
      <Typography
        variant="h4"
        align="center"
        style={{ margin: '20px 0', fontWeight: 'bold' }}
      >
        Gerenciamento de Colaboradores
      </Typography>

      <Typography variant="h6" align="left" style={{ marginBottom: 16 }}>
        {searchValue || situacao
          ? `Filtros: ${searchValue ? `"${searchValue}"` : ''} ${
              situacao ? `Situação: "${situacao}"` : ''
            }`
          : `Total de registros: ${totalRecords}`}
      </Typography>

      {/* Paginação superior */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" marginBottom={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Tabela de resultados */}
      <TableContainer component={Paper} elevation={3}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>CNPJ</strong>
              </TableCell>
              <TableCell>
                <strong>CPF</strong>
              </TableCell>
              <TableCell>
                <strong>Nome</strong>
              </TableCell>
              <TableCell>
                <strong>Local/Obra</strong>
              </TableCell>
              <TableCell>
                <strong>GHE</strong>
              </TableCell>
              <TableCell>
                <strong>Situação</strong>
              </TableCell>
              <TableCell>
                <strong>Ações</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  style={{ padding: '40px 0' }}
                >
                  <CircularProgress size={40} />
                  <Typography variant="body1" style={{ marginTop: 16 }}>
                    Carregando colaboradores...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : data.length > 0 ? (
              data.map((item) => (
                <TableRow key={item.id_colaboradores} hover>
                  <TableCell>{item.cnpj ? maskCNPJ(item.cnpj) : '-'}</TableCell>
                  <TableCell>
                    {item.cpf_colaborador ? maskCPF(item.cpf_colaborador) : '-'}
                  </TableCell>
                  <TableCell>{item.nome_completo || '-'}</TableCell>
                  <TableCell>{item.local_obra || '-'}</TableCell>
                  <TableCell>{item.ghe || '-'}</TableCell>
                  <TableCell>{item.situacao || '-'}</TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Link
                        to={{ pathname: '/editar-colaborador', state: item }}
                        style={{ textDecoration: 'none' }}
                      >
                        <IconButton aria-label="editar" color="primary">
                          <SettingsBackupRestore />
                        </IconButton>
                      </Link>
                      <IconButton
                        aria-label="deletar"
                        onClick={() => deleteColaborador(item)}
                        color="secondary"
                      >
                        <HighlightOff />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  style={{ padding: '40px 0' }}
                >
                  <Typography variant="h6">
                    {searchValue || situacao
                      ? 'Nenhum colaborador encontrado com os filtros aplicados'
                      : 'Nenhum registro encontrado'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação inferior */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" marginTop={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={dialogDelete} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Você está prestes a excluir o seguinte colaborador:
          </Typography>
          <Typography>
            <strong>Nome:</strong> {edit?.nome_completo || '-'}
          </Typography>
          <Typography>
            <strong>CPF:</strong>{' '}
            {edit?.cpf_colaborador ? maskCPF(edit.cpf_colaborador) : '-'}
          </Typography>
          <Typography>
            <strong>Matrícula:</strong> {edit?.matricula_empresa || '-'}
          </Typography>
          <Typography
            color="error"
            style={{ marginTop: 16, fontWeight: 'bold' }}
          >
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDialog}
            title="Cancelar"
            variantstyle="SECUNDARY"
            disabled={loading}
          />
          <Button
            onClick={confirmDelete}
            title={loading ? 'Excluindo...' : 'Confirmar Exclusão'}
            variantstyle="RED"
            disabled={loading}
            autoFocus
          />
        </DialogActions>
      </Dialog>

      {/* Snackbar de notificação */}
      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
